/**
 * ContradictionsTimeline — public vertical timeline of LLM-detected
 * contradictions between official WHO/CDC statements and press-reported actions.
 *
 * Design rules (uncodixify-ui.md):
 *   - No hover lift, no shadow escalation, no animate-pulse on badges.
 *   - Severity badges use semantic colour tokens with explicit dark variants.
 *   - transition-colors duration-150 only.
 *
 * RTL rules (rtl-support-arabic.md):
 *   - Logical CSS only: ms-*, me-*, text-start, border-s-*.
 *   - No rtl:flex-row-reverse — app sets dir="rtl" on <html> for Arabic.
 *   - ExternalLink icon mirrors via rtl:scale-x-[-1].
 *
 * Touch targets: external links have min-h-[44px] on their parent wrapper.
 *
 * Prompt #09 (timeline-contradictions-and-unanswered-questions).
 */

import type { TranslateFn } from "#/lib/i18n.js";
import { useContradictions } from "#/hooks/useContradictions.js";
import type { ContradictionItem } from "#/hooks/useContradictions.js";
import { ExternalLink } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ContradictionsTimelineProps {
  locale: string;
  t: TranslateFn;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function SeverityBadge({
  severity,
  t,
}: {
  severity: "HIGH" | "MEDIUM";
  t: TranslateFn;
}) {
  const label =
    severity === "HIGH"
      ? t("contradictions:contradictions.severity.high")
      : t("contradictions:contradictions.severity.medium");

  const colourClass =
    severity === "HIGH"
      ? "bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800"
      : "bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800";

  const dotClass =
    severity === "HIGH"
      ? "bg-red-500 dark:bg-red-400"
      : "bg-amber-500 dark:bg-amber-400";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium ${colourClass}`}
      role="status"
      aria-label={`${t("contradictions:contradictions.columns.severity")}: ${label}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function SourceLink({
  url,
  label,
}: {
  url: string;
  label: string;
}) {
  return (
    <div className="inline-flex min-h-[44px] items-center">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline transition-colors duration-150"
        aria-label={label}
      >
        <ExternalLink
          className="h-3.5 w-3.5 flex-shrink-0 rtl:scale-x-[-1]"
          aria-hidden="true"
        />
        <span className="sr-only">{label}</span>
      </a>
    </div>
  );
}

function ContradictionEntry({
  item,
  t,
  locale,
}: {
  item: ContradictionItem;
  t: TranslateFn;
  locale: string;
}) {
  const dateStr = new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
    item.datedAt,
  );

  return (
    <div className="relative flex gap-4">
      {/* Timeline spine dot */}
      <div className="flex flex-col items-center" aria-hidden="true">
        <div
          className={`mt-1 h-3 w-3 flex-shrink-0 rounded-full border-2 border-background ${
            item.severity === "HIGH"
              ? "bg-red-500 dark:bg-red-400"
              : "bg-amber-500 dark:bg-amber-400"
          }`}
        />
        <div className="w-px flex-1 bg-border" />
      </div>

      {/* Content */}
      <div className="pb-6 flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <time
            dateTime={item.datedAt.toISOString()}
            className="text-xs text-muted-foreground"
          >
            {dateStr}
          </time>
          <SeverityBadge severity={item.severity} t={t} />
        </div>

        <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
          {/* Official says */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  {t("contradictions:contradictions.says")}
                </p>
                <p className="text-sm text-foreground text-start">
                  {item.officialStatement}
                </p>
              </div>
              <SourceLink
                url={item.officialSourceUrl}
                label={t("contradictions:contradictions.viewOfficialSource")}
              />
            </div>
          </div>

          {/* Reported action */}
          <div className="px-4 py-3 bg-muted/30">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  {t("contradictions:contradictions.reported")}
                </p>
                <p className="text-sm text-foreground text-start">
                  {item.actualAction}
                </p>
              </div>
              <SourceLink
                url={item.actionSourceUrl}
                label={t("contradictions:contradictions.viewActionSource")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ContradictionsTimeline({
  locale,
  t,
}: ContradictionsTimelineProps) {
  const { data, isLoading, isError } = useContradictions();

  return (
    <section
      id="contradictions"
      aria-labelledby="contradictions-heading"
      className="space-y-4"
    >
      <div>
        <h2
          id="contradictions-heading"
          className="text-xl font-semibold text-foreground"
        >
          {t("contradictions:contradictions.heading")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("contradictions:contradictions.subheading")}
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t("contradictions:contradictions.loading")}
        </p>
      )}

      {isError && !isLoading && (
        <p
          className="text-sm text-destructive"
          role="alert"
          aria-live="assertive"
        >
          {t("contradictions:contradictions.error")}
        </p>
      )}

      {!isLoading && !isError && data?.length === 0 && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t("contradictions:contradictions.empty")}
        </p>
      )}

      {!isLoading && !isError && data && data.length > 0 && (
        <div className="relative">
          {data.map((item) => (
            <ContradictionEntry
              key={item.id}
              item={item}
              t={t}
              locale={locale}
            />
          ))}
        </div>
      )}
    </section>
  );
}
